import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Box,
  Checkbox,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import CompareIcon from '@mui/icons-material/Compare';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Loading from '../../../../components/loading/index';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import ImageModal from '../imageModal';
import { SkipBack, SkipForward } from 'lucide-react';
import { useImages } from '../hooks/useImages';
import { useImageActions } from '../hooks/useImageActions';
import FileTypeFilter from '../fileTypeFilter';
import { filterByFileType, isPdf } from '../fileType';

export default function ImageListWeb() {
  const { patientId } = useParams();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isNewImageModalOpen, setIsNewImageModalOpen] = useState(false);
  const [selectedImageToView, setSelectedImageToView] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const [showModalToDelete, setShowModalToDelete] = useState(false);

  const [selectedIndexFromImageList, setSelectedIndexFromImageList] = useState(null);
  const [hasPreviousImage, setHasPreviousImage] = useState(false);
  const [hasNextImage, setHasNextImage] = useState(false);

  const [selectedToCompare, setSelectedToCompare] = useState([]);
  const [imagesToCompare, setImagesToCompare] = useState(null);

  const [fileTypeFilter, setFileTypeFilter] = useState('all');

  const {
    images,
    isLoading: isImagesLoading,
    error: listError,
    refetch: getImagesFromPatient,
  } = useImages(patientId);
  const { remove, fetchUrl } = useImageActions();
  const visibleFiles = filterByFileType(images, fileTypeFilter);

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const onImageSaved = (returnFromApi) => {
    setIsNewImageModalOpen(false);
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    getImagesFromPatient();
  };

  const deleteImage = (imageId) => {
    setLoading(true);
    remove(imageId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getImagesFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const menuClose = () => {
    setAnchorEl(null);
  };

  const confirmDelete = () => {
    setShowModalToDelete(false);
    if (selectedImage) {
      deleteImage(selectedImage.id);
    }
  };

  function openImage(image) {
    setLoading(true);

    const indexFromImage = visibleFiles.findIndex((img) => img.id === image.id);
    setSelectedIndexFromImageList(indexFromImage);
    setHasNextImage(Boolean(visibleFiles?.[indexFromImage + 1]));
    setHasPreviousImage(Boolean(visibleFiles?.[indexFromImage - 1]));
    fetchUrl(image.id)
      .then((returnFromApi) => {
        setSelectedImageToView({ url: returnFromApi.data, isPdf: isPdf(image) });
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  }

  function openModalToDelete() {
    setShowModalToDelete(true);
  }

  const goPreviousImage = () => {
    const newIndex = selectedIndexFromImageList - 1;
    const selectedImage = visibleFiles?.[newIndex];
    setSelectedIndexFromImageList(newIndex);
    setHasNextImage(Boolean(visibleFiles?.[newIndex + 1]));
    setHasPreviousImage(Boolean(visibleFiles?.[newIndex - 1]));

    openImage(selectedImage);
  };

  const goNextImage = () => {
    const newIndex = selectedIndexFromImageList + 1;
    const selectedImage = visibleFiles?.[newIndex];
    setSelectedIndexFromImageList(newIndex);
    setHasNextImage(Boolean(visibleFiles?.[newIndex + 1]));
    setHasPreviousImage(Boolean(visibleFiles?.[newIndex - 1]));

    openImage(selectedImage);
  };

  const isSelectedToCompare = (image) => selectedToCompare.some((img) => img.id === image.id);

  const toggleCompareSelection = (image) => {
    setSelectedToCompare((previousSelection) => {
      if (previousSelection.some((img) => img.id === image.id)) {
        return previousSelection.filter((img) => img.id !== image.id);
      }
      if (previousSelection.length >= 2) {
        return previousSelection;
      }
      return [...previousSelection, image];
    });
  };

  const openCompare = () => {
    if (selectedToCompare.length !== 2) return;
    setLoading(true);
    Promise.all(selectedToCompare.map((image) => fetchUrl(image.id)))
      .then((responses) => {
        setImagesToCompare(
          selectedToCompare.map((image, index) => ({
            ...image,
            url: responses[index].data,
          })),
        );
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Loading isToHidden={!isLoading && !isImagesLoading} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Imagens e Documentos
            </Typography>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Marque duas imagens na lista para comparar e visualizá-las lado a lado.">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<CompareIcon />}
                    onClick={openCompare}
                    disabled={selectedToCompare.length !== 2}
                  >
                    Comparar Lado a Lado
                    {selectedToCompare.length > 0 ? ` (${selectedToCompare.length}/2)` : ''}
                  </Button>
                </span>
              </Tooltip>
              <Button variant="outlined" onClick={() => setIsNewImageModalOpen(true)}>
                Adicionar Arquivo
              </Button>
            </Box>
          }
        />
        <CardContent>
          <FileTypeFilter value={fileTypeFilter} onChange={setFileTypeFilter} />
          {!isLoading && visibleFiles.length === 0 ? (
            <Typography align="center" sx={{ py: 4 }}>
              Nenhum arquivo cadastrado ainda.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleFiles.map((image) => (
                    <TableRow key={image.id} hover>
                      <TableCell padding="checkbox">
                        <Tooltip
                          title={
                            isPdf(image)
                              ? 'Documentos PDF não podem ser comparados.'
                              : !isSelectedToCompare(image) && selectedToCompare.length >= 2
                                ? 'Você só pode comparar duas imagens por vez.'
                                : 'Selecionar para comparar'
                          }
                        >
                          <span>
                            <Checkbox
                              checked={isSelectedToCompare(image)}
                              disabled={
                                isPdf(image) ||
                                (!isSelectedToCompare(image) && selectedToCompare.length >= 2)
                              }
                              onChange={() => toggleCompareSelection(image)}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: '20%' }}>{image.date}</TableCell>
                      <TableCell>{image.description}</TableCell>
                      <TableCell sx={{ width: 84 }}>
                        {isPdf(image) ? (
                          <Box
                            onClick={() => openImage(image)}
                            sx={{
                              width: 100,
                              height: 100,
                              borderRadius: 1,
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <PictureAsPdfIcon color="error" sx={{ fontSize: 48 }} />
                            <Typography variant="caption" color="text.secondary">
                              PDF
                            </Typography>
                          </Box>
                        ) : image?.thumbnailUrl ? (
                          <Box
                            component="img"
                            src={image.thumbnailUrl}
                            alt={image.description || 'Miniatura'}
                            loading="lazy"
                            onClick={() => openImage(image)}
                            sx={{
                              width: 100,
                              height: 100,
                              borderRadius: 1,
                              objectFit: 'cover',
                              cursor: 'pointer',
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                            }}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setSelectedImage(image);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ImageModal
        open={isNewImageModalOpen}
        onCloseImageModal={() => setIsNewImageModalOpen(false)}
        onImageSaved={onImageSaved}
        patientId={patientId}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={menuClose}>
        <MenuItem
          onClick={() => {
            openImage(selectedImage);
            menuClose();
          }}
        >
          Visualizar
        </MenuItem>
        <MenuItem
          onClick={() => {
            openModalToDelete();
            menuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          Excluir
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(selectedImageToView)}
        maxWidth="xl"
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' && hasPreviousImage) {
            goPreviousImage();
          }
          if (e.key === 'ArrowRight' && hasNextImage) {
            goNextImage();
          }
        }}
      >
        <Loading isToHidden={!isLoading && !isImagesLoading} />
        <Tooltip title="Fechar">
          <IconButton
            aria-label="close"
            onClick={() => setSelectedImageToView(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              backgroundColor: 'white',
              color: 'black',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'lightgray',
              },
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Tooltip>

        {selectedImageToView?.isPdf ? (
          <Box sx={{ width: '80vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1, pr: 8 }}>
              <Button
                href={selectedImageToView.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon />}
              >
                Abrir em nova aba
              </Button>
            </Box>
            <Box
              component="iframe"
              src={selectedImageToView.url}
              title="Documento PDF"
              onLoad={() => setLoading(false)}
              sx={{ flex: 1, width: '100%', border: 0 }}
            />
          </Box>
        ) : (
          <img
            src={selectedImageToView?.url}
            alt="Imagem"
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            onLoad={() => setLoading(false)}
          />
        )}

        <Tooltip title="Anterior">
          <span>
            <IconButton
              aria-label="anterior"
              onClick={goPreviousImage}
              disabled={!hasPreviousImage}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                zIndex: 2,
                '&:hover': { backgroundColor: 'lightgray' },
              }}
            >
              <SkipBack />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Próxima">
          <span>
            <IconButton
              aria-label="próxima"
              onClick={goNextImage}
              disabled={!hasNextImage}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                zIndex: 2,
                '&:hover': { backgroundColor: 'lightgray' },
              }}
            >
              <SkipForward />
            </IconButton>
          </span>
        </Tooltip>
      </Dialog>

      <Dialog open={Boolean(imagesToCompare)} maxWidth="xl" fullWidth>
        <Loading isToHidden={!isLoading && !isImagesLoading} />
        <Tooltip title="Fechar">
          <IconButton
            aria-label="close"
            onClick={() => setImagesToCompare(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 2,
              backgroundColor: 'white',
              color: 'black',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'lightgray',
              },
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Tooltip>

        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 2,
            }}
          >
            {(imagesToCompare || []).map((image) => (
              <Box
                key={image.id}
                sx={{
                  flex: '0 1 auto',
                  maxWidth: { xs: '100%', md: '50%' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src={image.url}
                  alt={image.description || 'Imagem'}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                  }}
                />
                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>
                  {image.date}
                </Typography>
                {image.description ? (
                  <Typography variant="body2" color="text.secondary" align="center">
                    {image.description}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showModalToDelete}
        onClose={() => setShowModalToDelete(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmar Exclusão'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja excluir este arquivo? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModalToDelete(false)}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
